// copied and edited from packages/ui/src/components/FlavorFinder/components/Question/FlavorFinderQuestion.tsx
import type { FC } from "react";
import { useCallback } from "react";

import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type { FlavorFinderAnswerDetails, FlavorFinderQuestions, ViewType } from "../../types/FlavorFinderTypes";
import { FlavorFinderAnswer } from "../Answer/FlavorFinderAnswer";
import { FlavorFinderBackButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";

import styles from "./FlavorFinderQuestion.module.scss";

type FlavorFinderQuestionProps = {
  question: FlavorFinderQuestions;
  nextCtaLabel: string;
  backCtaLabel: string;
  questionNumberIndicator: string;
  selectionIndicator?: string;
  totalNumberOfQuestions: number;
  hideNextButton?: boolean;
};

export const FlavorFinderQuestion: FC<FlavorFinderQuestionProps> = ({
  question,
  backCtaLabel,
  questionNumberIndicator,
  selectionIndicator,
  totalNumberOfQuestions,
  hideNextButton = false,
}) => {
  const { question: questionDetails, possibleAnswers = [] } = question;

  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    setCurrentQuestion,
    questions,
    setCurrentView,
    setUserJourney,
    userJourney,
    setQuizResult,
    flavorFinderData,
    handlePreviousStepClick,
  } = useFlavorFinderContext();

  const { __ } = useTranslations();

  const counterLabel = `${questionNumberIndicator} ${currentQuestionIndex + 1}`;
  const finalQuestion = flavorFinderData.finalQuestion;
  const { isSmall } = useCurrentBreakpoint();

  const selectedAnswerIdForCurrentQuestion = userJourney.find(
    (step) => step.question?.question?.id === questionDetails?.id
  );
  const handleNextStepClick = useCallback(() => {
    const nextQuestion = questions.find(
      (question) => question.onAnswerId === selectedAnswerIdForCurrentQuestion?.answer_id
    );

    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);

    if (nextQuestion) {
      handleQuestionOrResult(nextQuestion);
    }
  }, [
    finalQuestion,
    questions,
    selectedAnswerIdForCurrentQuestion?.answer_id,
    currentQuestionIndex,
    setCurrentQuestionIndex,
  ]);

  const handleQuestionOrResult = (nextQuestion: FlavorFinderQuestions) => {
    if (nextQuestion?.question) {
      setCurrentQuestion(nextQuestion);
      updateUserJourney("question", nextQuestion);
    } else if (nextQuestion?.results) {
      setQuizResults(nextQuestion);
      updateFinalStep();
    }
  };

  const updateUserJourney = (view: ViewType, question?: FlavorFinderQuestions) => {
    setUserJourney((prevJourney) => [...prevJourney, { view, question }]);
  };

  const setQuizResults = (nextQuestion: FlavorFinderQuestions) => {
    setQuizResult({
      resultsLink: nextQuestion?.resultsLink ?? "",
      results: nextQuestion.results ?? [],
      headerUrl: nextQuestion.headerUrl ?? "",
      resultsLinkWithDevice: nextQuestion.resultsLinkWithDevice ?? "",
    });
  };

  const updateFinalStep = () => {
    if (finalQuestion) {
      updateUserJourney("optional", finalQuestion);
      setCurrentView("optional");
    } else {
      updateUserJourney("consent");
      setCurrentView("consent");
    }
  };

  const handleAnswerClick = useCallback(
    (answerId: string) => {
      const updatedJourney = [...userJourney];
      const currentStepIndex = updatedJourney.findIndex((step) => step.question?.question?.id === questionDetails?.id);
      const selectedAnswer = possibleAnswers.find((answer) => answer.id === answerId);

      if (currentStepIndex > -1) {
        updatedJourney[currentStepIndex].answer_id = answerId;
        updatedJourney[currentStepIndex].reference_name = selectedAnswer?.referenceName;
        updatedJourney[currentStepIndex].local_translation = selectedAnswer?.localTranslation;
      } else {
        updatedJourney.push({
          view: "question",
          question: question,
          answer_id: answerId,
          reference_name: selectedAnswer?.referenceName,
          local_translation: selectedAnswer?.localTranslation,
        });
      }

      setUserJourney(updatedJourney);
      if (hideNextButton) {
        handleNextStepClick();
      }
    },
    [handleNextStepClick, hideNextButton, possibleAnswers, question, questionDetails?.id, setUserJourney, userJourney]
  );

  return (
    <div className={styles.questionsWrapper} data-testid="flavor-finder-question">
      {isSmall && <FlavorFinderBackButton onClick={handlePreviousStepClick} label={backCtaLabel} mobileHidden />}
      <div className={styles.questionContainer}>
        <div className={styles.question}>
          <FlavorFinderHeader
            title={questionDetails?.title}
            subtitle={selectionIndicator}
            infoText={`${counterLabel} ${__("flavorfinder.numbers.separator")} ${totalNumberOfQuestions}`}
          >
            {!isSmall && <FlavorFinderBackButton onClick={handlePreviousStepClick} label={backCtaLabel} mobileHidden />}
          </FlavorFinderHeader>
          <div
            className={classNames(styles.answers, {
              [styles.answersCount2]: possibleAnswers?.length === 2,
            })}
          >
            {possibleAnswers?.map((answer: FlavorFinderAnswerDetails) => {
              return (
                <FlavorFinderAnswer
                  onAnswerClick={handleAnswerClick}
                  answer={answer}
                  key={answer.id}
                  isActive={answer.id === selectedAnswerIdForCurrentQuestion?.answer_id}
                />
              );
            })}
          </div>
          <div className={styles.footer}>
            <FlavorFinderBackButton onClick={handlePreviousStepClick} label={backCtaLabel} />
          </div>
        </div>
      </div>
    </div>
  );
};
